import { FC, useEffect, useRef } from 'react';
import theme from 'config/theme';
import { ENV_CONFIG } from 'config/environment';
import { ButtonBrand } from 'components/ButtonBrand';
import Icon, { IconNames } from 'components/Icon';
import { Image } from 'components/Image';
import { getPathWithRef } from 'helpers/utils';
import { useAppDispatch, useAppSelector } from 'helpers/hooks';
import { getRecipients } from 'store/recipients/actions';
import { getRecipientById, getRecipientsState } from 'selectors/recipients';
import { selectTodaysSpotlightCard } from './selectTodaysCard';
import { RecipientSpotlightComponentDetails } from './types';
import { useLocation } from 'react-router-dom';

interface Props {
  config?: RecipientSpotlightComponentDetails;
  onDonateClick?: (linkUrl: string) => void;
  onView?: (linkUrl: string) => void;
}

export const RecipientSpotlightCard: FC<Props> = ({ config, onDonateClick, onView }) => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { data, isLoading } = useAppSelector(getRecipientsState);
  const todaysCard = selectTodaysSpotlightCard(config?.cards);
  const recipient = useAppSelector(state => getRecipientById(state, todaysCard?.recipient_id));
  const requestLocationPath = location.pathname;
  const hasTrackedView = useRef(false);

  useEffect(() => {
    if (!todaysCard?.recipient_id || isLoading) {
      return;
    }

    const alreadyLoaded = data?.recipients?.some(
      item => item.recipient_id === todaysCard.recipient_id
    );
    if (alreadyLoaded) {
      return;
    }

    dispatch(
      getRecipients({
        amount: 1,
        recipient_ids: [todaysCard.recipient_id],
        payment_processor: todaysCard.payment_processor,
        request_location_path: requestLocationPath,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [todaysCard?.recipient_id]);

  useEffect(() => {
    if (!todaysCard?.cta_link || !recipient || hasTrackedView.current) {
      return;
    }
    hasTrackedView.current = true;
    onView?.(todaysCard.cta_link);
  }, [todaysCard?.cta_link, recipient]);

  if (!todaysCard || !recipient) {
    return null;
  }

  const backgroundImageUrl = todaysCard.image
    ? `${ENV_CONFIG().CLOUD_STORAGE.URL}/${todaysCard.image}`
    : undefined;

  return (
    <div className="recipient-spotlight-card">
      <div
        className="recipient-spotlight-card__image-container"
        style={backgroundImageUrl ? { backgroundImage: `url(${backgroundImageUrl})` } : undefined}
      ></div>
      <div className="recipient-spotlight-card__texts-container">
        <div className="recipient-spotlight-card__tooltip">
          <Icon name={IconNames.Star} size={12} color={theme.colors.oathBlue} />
          <span className="eyebrow-text">{todaysCard.tooltip}</span>
        </div>
        <span className="h4-text semi-bold">{todaysCard.title}</span>
        <div className="recipient-spotlight-card__recipient-row">
          <Image
            alt={`Recipient ${recipient.recipient_name}`}
            src={`${ENV_CONFIG().CLOUD_STORAGE.URL}/recipients/rec_${recipient.misc_ids?.photo_id}.jpg`}
            fallbackImgType="candidate_square"
            className="recipient-spotlight-card__avatar"
          />
          <div className="recipient-spotlight-card__recipient-details">
            <span className="body-text semi-bold">{recipient.recipient_name}</span>
            {(recipient.race?.election.display_info || recipient.race?.election.state) && (
              <span className="body-small-text color-dark-gray">
                {[recipient.race?.election.display_info, recipient.race?.election.state]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            )}
          </div>
          {recipient.impact_score && (
            <div className="recipient-spotlight-card__impact-score">
              <span className="h4-text color-oathBlue semi-bold">
                {parseFloat(recipient.impact_score).toFixed(1)}
              </span>
              <span className="utility-text color-dark-gray recipient-spotlight-card__impact-score-label">
                Impact
              </span>
            </div>
          )}
        </div>
        {todaysCard.cta_link && (
          <ButtonBrand
            to={getPathWithRef(todaysCard.cta_link)}
            onClick={() => onDonateClick?.(todaysCard.cta_link)}
            $backgroundColor={theme.colors.oathBlue}
            $width="100%"
            className="recipient-spotlight-card__cta"
          >
            {todaysCard.cta}
          </ButtonBrand>
        )}
      </div>
    </div>
  );
};
